import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLoader } from "@/hooks/LoaderProvider";
import { useNavigate } from "react-router-dom";
import { DownOutlined, PlusOutlined, Dropdown } from "@/config/antd";
import {
  selectManifestos,
  selectManifestoLoading,
  selectFetchedManifestos,
  selectManifestoTotal,
  deleteManifesto,
} from "@/store/redux/slices/manifesto/manifesto_slice";
import { fetchManifestoByAny } from "@/store/redux/slices/manifesto/manifesto_slice";
import PageHeader from "@/components/layout/Header/Page_Header";
import Breadcrumb from "@/components/specific/Breadcrumb";
import Button from "@/components/common/Button";
import SearchFilterBar from "@/components/specific/Search_&_Filter";
import CustomPagination from "@/components/specific/Pagination";
import ManifestoCard from "./components/Manifesto_Card";
import EmptyState from "./components/Empty_State";
import ManifestoHomeTable from "./components/ManifestoHomeTable";
import TrashIcon from "@/assets/icons/web_Icons/trash_red.svg";
import Success from "@/components/common/alerts/Success";
import { getOrganizationId } from "@/utils/token_functions";
import Delete from "@/components/common/alerts/Delete";
import { selectUserProfile } from "@/store/redux/slices/user/user_slice";

const Manifesto = () => {
  const dispatch = useDispatch();
  const manifestos = useSelector(selectManifestos);
  const fetchedManifestosByOrg = useSelector(selectFetchedManifestos);
  const totalManifestosCount = useSelector(selectManifestoTotal);
  const isLoading = useSelector(selectManifestoLoading);

  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("all"); // date filter: 'all' | 'today' | 'thisWeek' | 'thisMonth'
  const [, setTotalManifestos] = useState(0);
  const [selectedManifestos, setSelectedManifestos] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("all");

  // user profile
  const userProfile = useSelector(selectUserProfile);

  // State for success feedback modal
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [deletedManifestoName, setDeletedManifestoName] = useState("");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedManifestoToDelete, setSelectedManifestoToDelete] =
    useState(null);

  const getManifestosToUse = useCallback(() => {
    // If we have data from fetchedManifestosByOrg, use it, otherwise fall back to manifestos
    const dataSource =
      fetchedManifestosByOrg?.length > 0 ? fetchedManifestosByOrg : manifestos;

    if (dataSource && dataSource.length > 0 && dataSource[0]?.data) {
      return dataSource[0].data;
    }

    return dataSource;
  }, [fetchedManifestosByOrg, manifestos]);

  useEffect(() => {
    const manifestoCount = getManifestosToUse().length;
    setTotalManifestos(
      manifestoCount > 0 ? manifestoCount : totalManifestosCount
    );
  }, [getManifestosToUse, totalManifestosCount]);

  useEffect(() => {
    const fetchManifestos = async () => {
      const organizationID = getOrganizationId();

      showLoader();
      try {
        await dispatch(
          fetchManifestoByAny({
            key: "organizationID",
            value: organizationID,
          })
        ).unwrap();

        // Optionally keep the original fetch if needed for backward compatibility
        // await dispatch(getAllManifestos()).unwrap();
      } catch (error) {
        console.error("Error fetching manifestos:", error);
      } finally {
        hideLoader();
      }
    };

    fetchManifestos();
  }, [dispatch, hideLoader, showLoader]);

  // Calculate counts for filters
  const getManifestoCounts = useCallback(() => {
    const manifestosToCount = getManifestosToUse();
    if (!manifestosToCount || manifestosToCount.length === 0) {
      return { total: 0, expired: 0, upcoming: 0, active: 0 };
    }

    const currentDate = new Date();

    const counts = manifestosToCount.reduce(
      (acc, manifesto) => {
        // Determine if manifesto is expired based on endDate (if available)
        const endDate = manifesto.endDate ? new Date(manifesto.endDate) : null;
        const startDate = manifesto.startDate
          ? new Date(manifesto.startDate)
          : null;

        if (endDate && endDate < currentDate) {
          acc.expired += 1;
        } else if (startDate && startDate > currentDate) {
          acc.upcoming += 1;
        } else {
          acc.active += 1;
        }

        return acc;
      },
      { total: manifestosToCount.length, expired: 0, upcoming: 0, active: 0 }
    );

    return counts;
  }, [getManifestosToUse]);

  const applyDateFilter = useCallback(
    (list) => {
      if (!filterValue || filterValue === "all") return list;

      const now = new Date();

      return list.filter((manifesto) => {
        // Use createdAt as the primary date for filtering
        const createdAt = manifesto.createdAt
          ? new Date(manifesto.createdAt)
          : null;

        if (!createdAt) return true; // keep records without a date

        const sameDay =
          createdAt.getDate() === now.getDate() &&
          createdAt.getMonth() === now.getMonth() &&
          createdAt.getFullYear() === now.getFullYear();

        if (filterValue === "today") {
          return sameDay;
        }

        if (filterValue === "thisWeek") {
          const startOfWeek = new Date(now);
          startOfWeek.setHours(0, 0, 0, 0);
          startOfWeek.setDate(now.getDate() - now.getDay());

          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 7);

          return createdAt >= startOfWeek && createdAt < endOfWeek;
        }

        if (filterValue === "thisMonth") {
          return (
            createdAt.getMonth() === now.getMonth() &&
            createdAt.getFullYear() === now.getFullYear()
          );
        }

        return true;
      });
    },
    [filterValue]
  );

  const getFilteredCardsData = useCallback(() => {
    let filtered = getManifestosToUse();
    const currentDate = new Date();

    // Apply search filter - use description as title if name is not available
    if (searchValue.trim()) {
      filtered = filtered.filter((manifesto) => {
        // Safely get lowercase values or empty strings as fallbacks
        const nameMatch = manifesto.name?.toLowerCase() || "";
        const descriptionMatch = manifesto.description?.toLowerCase() || "";

        // Check if either contains the search value
        return (
          nameMatch.includes(searchValue.toLowerCase()) ||
          descriptionMatch.includes(searchValue.toLowerCase())
        );
      });
    }

    // Apply status filter
    switch (activeFilter) {
      case "expired":
        filtered = filtered.filter((manifesto) => {
          const endDate = manifesto.endDate
            ? new Date(manifesto.endDate)
            : null;
          return endDate && endDate < currentDate;
        });
        break;
      case "upcoming":
        filtered = filtered.filter((manifesto) => {
          const startDate = manifesto.startDate
            ? new Date(manifesto.startDate)
            : null;
          return startDate && startDate > currentDate;
        });
        break;
      default:
        break;
    }

    // Apply date filter (Today / This Week / This Month)
    filtered = applyDateFilter(filtered);

    // Transform for cards
    return filtered.map((manifesto) => {
      // Determine status
      const endDate = manifesto.endDate ? new Date(manifesto.endDate) : null;
      const startDate = manifesto.startDate
        ? new Date(manifesto.startDate)
        : null;
      let status = "Published";

      if (endDate && endDate < currentDate) {
        status = "Expired";
      } else if (startDate && startDate > currentDate) {
        status = "Upcoming";
      }

      return {
        id: manifesto._id,
        title: manifesto.name || manifesto.description, // Use description as fallback for title
        date: `Published on ${new Date(manifesto.createdAt).toLocaleDateString(
          "en-US",
          { month: "long", year: "numeric" }
        )}`,
        description: manifesto.description,
        fileUrl: manifesto.fileUrl,
        status: status,
        isExpired: status === "Expired",
        campaigns: [],
        author: userProfile.fullname,
        avatar: userProfile.profileImage,
        endDate: manifesto.endDate,
        startDate: manifesto.startDate,
      };
    });
  }, [getManifestosToUse, searchValue, activeFilter, applyDateFilter, userProfile]);

  const getFilteredTableData = useCallback(() => {
    // Similar to getFilteredCardsData but with tableData format
    let filtered = getManifestosToUse();
    // ... rest of the existing filter logic ...

    // Apply search filter - use description as title if name is not available
    if (searchValue.trim()) {
      filtered = filtered.filter((manifesto) => {
        // Safely get lowercase values or empty strings as fallbacks
        const nameMatch = manifesto.name?.toLowerCase() || "";
        const descriptionMatch = manifesto.description?.toLowerCase() || "";

        // Check if either contains the search value
        return (
          nameMatch.includes(searchValue.toLowerCase()) ||
          descriptionMatch.includes(searchValue.toLowerCase())
        );
      });
    }

    // Apply status filter
    const currentDate = new Date();
    switch (activeFilter) {
      case "expired":
        filtered = filtered.filter((manifesto) => {
          const endDate = manifesto.endDate
            ? new Date(manifesto.endDate)
            : null;
          return endDate && endDate < currentDate;
        });
        break;
      case "upcoming":
        filtered = filtered.filter((manifesto) => {
          const startDate = manifesto.startDate
            ? new Date(manifesto.startDate)
            : null;
          return startDate && startDate > currentDate;
        });
        break;
      default:
        break;
    }

    // Apply date filter (Today / This Week / This Month)
    filtered = applyDateFilter(filtered);

    return filtered.map((manifesto) => {
      // Transform manifesto data for table
      const endDate = manifesto.endDate ? new Date(manifesto.endDate) : null;
      const startDate = manifesto.startDate
        ? new Date(manifesto.startDate)
        : null;
      let status = "Published";

      if (endDate && endDate < currentDate) {
        status = "Expired";
      } else if (startDate && startDate > currentDate) {
        status = "Upcoming";
      }

      return {
        id: manifesto._id,
        title: manifesto.name || manifesto.description, // Use description as fallback for title
        description: manifesto.description,
        fileUrl: manifesto.fileUrl,
        publishDate: new Date(manifesto.createdAt).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
        status: status,
        endDate: manifesto.endDate,
        startDate: manifesto.startDate,
        author: userProfile.fullname,
      };
    });
  }, [getManifestosToUse, searchValue, activeFilter, applyDateFilter, userProfile]);

  const handleRefresh = useCallback(async () => {
    // 1. Reset pagination to first page when refreshing
    setCurrentPage(1);

    // 2. Clear the search term
    setSearchValue("");

    // 3. Reset the active filter to 'all' to show all data
    setActiveFilter("all");

    // 4. Reset the display text for the filter button
    setFilterValue("all");

    // 5. Fetch fresh data
    const organizationID = getOrganizationId();

    showLoader();
    try {
      // Refresh manifestos by organization ID
      await dispatch(
        fetchManifestoByAny({
          key: "organizationID",
          value: organizationID,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error refreshing manifestos:", error);
    } finally {
      hideLoader();
    }
  }, [dispatch, showLoader, hideLoader]);

  if (isLoading && getManifestosToUse().length === 0) {
    return null;
  }

  // Format manifesto data for cards and table
  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleFilterChange = (infoOrKey) => {
    // Support both AntD Dropdown ({ key }) and raw string from SearchFilterBar
    const key =
      typeof infoOrKey === "string" ? infoOrKey : infoOrKey?.key ?? "today";
    setFilterValue(key);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleAddNew = () => {
    if (selectedManifestos.length > 0) {
      // Show delete confirmation dialog for bulk delete
      setIsDeleteModalVisible(true);
    } else {
      // Navigate to add new manifesto page
      showLoader();
      setTimeout(() => {
        hideLoader();
        navigate("/dashboard/manifesto/add");
      }, 2000);
    }
  };

  const handleDelete = async (id, title) => {
    // If called with a specific ID, it's an individual delete
    if (id) {
      setSelectedManifestoToDelete({ id, title });
      setIsDeleteModalVisible(true);
    } else {
      // Otherwise, it's a bulk delete coming from the confirm dialog
      if (selectedManifestos.length === 0) return;

      showLoader();
      try {
        // Get the titles of manifestos being deleted for the success message
        const manifestosToDelete = getManifestosToUse().filter((manifesto) =>
          selectedManifestos.includes(manifesto._id)
        );
        const manifestoCount = manifestosToDelete.length;

        // If only one manifesto, use its title, otherwise use count
        if (manifestoCount === 1) {
          setDeletedManifestoName(manifestosToDelete[0].name);
        } else {
          setDeletedManifestoName(`${manifestoCount} manifestos`);
        }

        // Delete all selected manifestos
        await dispatch(deleteManifesto(selectedManifestos)).unwrap();

        // Refresh data and reset selection
        await handleRefresh();
        setSelectedManifestos([]);
        setSuccessModalVisible(true);
      } catch (error) {
        console.error("Bulk delete failed:", error);
      } finally {
        hideLoader();
      }
    }
  };

  // Process a deletion confirmation
  const handleDeleteConfirm = async () => {
    if (selectedManifestoToDelete) {
      setIsDeleteModalVisible(false);
      // Individual delete
      showLoader();
      try {
        setDeletedManifestoName(selectedManifestoToDelete.title);
        await dispatch(deleteManifesto(selectedManifestoToDelete.id)).unwrap();
        await handleRefresh();
        setSuccessModalVisible(true);
      } catch (error) {
        console.error("Delete failed:", error);
      } finally {
        hideLoader();
      }
    } else {
      // Bulk delete
      setIsDeleteModalVisible(false);
      await handleDelete();
    }
  };

  const handleSuccessClose = () => {
    setSuccessModalVisible(false);
    handleRefresh(); // Use the updated refresh function
  };

  const handleModify = (id) => {
    showLoader();
    navigate(`/dashboard/manifesto/edit/${id}`);
    hideLoader();
  };

  // Function to refresh data after table changes
  const handleTableChange = () => {
    handleRefresh(); // Use the updated refresh function
  };

  const filterOptions = {
    items: [
      { key: "all", label: "All Time" },
      { key: "today", label: "Today" },
      { key: "thisWeek", label: "This Week" },
      { key: "thisMonth", label: "This Month" },
    ],
    onClick: handleFilterChange,
    className: "custom-dropdown-menu",
  };

  const moreOptionsMenu = {
    items: [
      { key: "1", label: "Export" },
      { key: "2", label: "Print" },
    ],
  };

  // Define sort options based on your filter buttons
  const sortOptions = [
    { key: "all", label: "All Manifestos" },
    { key: "expired", label: "Expired" },
    { key: "upcoming", label: "Upcoming" },
  ];

  const cardData = getFilteredCardsData();
  const tableData = getFilteredTableData();

  // Get the counts
  const manifestoCounts = getManifestoCounts();
  const manifestoCount = getManifestosToUse().length;

  // breadcrumb items
  const items = [{ title: "Manifesto", link: "dashboard/manifesto" }];

  return (
    <div className="w-full my-6">
      <Breadcrumb items={items} />

      <PageHeader title="Manifesto" />

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-5">
          <div className="flex flex-col text-left">
            <h1
              className="heading-medium text-lighterGrey"
              data-testid="manifesto-count">
              {manifestoCount} Total Manifestos
            </h1>
            <h1 className="text-regular text-lighterGrey">
              Total number of Campaigns created
            </h1>
          </div>
          <div className="items-center hidden gap-2 lg:flex">
            <Button
              text={`All Manifestos (${manifestoCount})`}
              color="primary"
              className="px-2 py-1 text-sm sm:px-3 sm:py-2 sm:text-small min-w-[80px] sm:min-w-[100px]"
              iconPosition="left"
              customBgColor={activeFilter === "all" ? "primary" : "white"}
              customTextColor={activeFilter === "all" ? "white" : "primary"}
              onClick={() => setActiveFilter("all")}
            />
            <Button
              text={`Expired (${manifestoCounts.expired})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "expired" ? "primary" : "white"}
              customTextColor={activeFilter === "expired" ? "white" : "primary"}
              onClick={() => setActiveFilter("expired")}
            />
            <Button
              text={`Upcoming (${manifestoCounts.upcoming})`}
              className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
              customBgColor={activeFilter === "upcoming" ? "primary" : "white"}
              customTextColor={
                activeFilter === "upcoming" ? "white" : "primary"
              }
              onClick={() => setActiveFilter("upcoming")}
            />
          </div>
        </div>
        <div className="items-center hidden gap-3 lg:flex">
          <Button
            text={
              selectedManifestos.length != 0
                ? "DELETE SELECTED"
                : "ADD NEW MANIFESTO"
            }
            icon={
              selectedManifestos.length != 0 ? (
                <img src={TrashIcon} />
              ) : (
                <PlusOutlined />
              )
            }
            color={selectedManifestos.length != 0 ? "outline" : "primary"}
            className="border border-primary font-normal text-[11px] !px-3 !py-2 h-8 whitespace-nowrap"
            iconPosition="left"
            customBgColor={selectedManifestos.length != 0 ? "" : "white"}
            customTextColor={selectedManifestos.length != 0 ? "red" : "primary"}
            onClick={handleAddNew}
          />
          <Dropdown menu={filterOptions} trigger={["click"]}>
          <Button
              text={
                filterOptions.items.find((opt) => opt.key === filterValue)
                  ?.label || "FILTER BY"
              }
            icon={<DownOutlined />}
            iconPosition="right"
            className="border border-primary font-normal text-sm !px-3 !py-2 h-8" // Added size adjustments
            customBgColor="white"
            customTextColor="black"
          />
          </Dropdown>
        </div>
        <Button
          text="ADD NEW"
          icon={<PlusOutlined />}
          color="primary"
          className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] whitespace-nowrap sm:min-w-[100px] lg:hidden"
          iconPosition="left"
          customBgColor="primary"
          customTextColor="white"
          onClick={handleAddNew}
        />
      </div>

      <SearchFilterBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onFilterChange={(value) => {
          setActiveFilter(value);
        }}
        filterValue={filterValue}
        filterOptions={filterOptions}
        moreOptionsMenu={moreOptionsMenu}
        placeholder="Search manifesto..."
        filterType="dropdown"
        sortOptions={sortOptions}
        searchValue={searchValue}
      />

      {cardData.length > 0 ? (
        <>
          <div className="hidden lg:block">
            <ManifestoHomeTable
              data={tableData}
              setSelectedManifestos={setSelectedManifestos}
              onDelete={handleDelete}
              onModify={handleModify}
              onTableChange={handleTableChange}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2 lg:hidden">
            {cardData.map((manifesto) => (
              <ManifestoCard
                key={manifesto.id}
                {...manifesto}
                onDelete={handleDelete}
                onModify={handleModify}
                onCardChange={handleTableChange}
              />
            ))}
          </div>

          <div className="mt-6">
            <CustomPagination
              current={currentPage}
              total={manifestoCount}
              pageSize={pageSize}
              onChange={handlePageChange}
              className="pb-8"
            />
          </div>
        </>
      ) : (
        <EmptyState onSetup={handleAddNew} />
      )}

      {/* Success Modal */}
      <Success
        isVisible={successModalVisible}
        onClose={handleSuccessClose}
        header="Manifesto Deleted"
        paragraph={`"${deletedManifestoName}" has been successfully deleted.`}
        buttonText="Done"
        showCancelButton={false}
        onButtonClick={handleSuccessClose}
      />

      {/* Add Delete confirmation modal */}
      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Manifesto?"
        paragraph={
          selectedManifestoToDelete
            ? `Are you sure you want to delete "${selectedManifestoToDelete.title}"?`
            : `Are you sure you want to delete ${selectedManifestos.length} selected manifestos? This action cannot be undone.`
        }
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export default Manifesto;
